import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const SEVERITY_STYLES = {
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  warning: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  critical: 'bg-purple-50 text-purple-700 border-purple-200',
};

const STATUS_STYLES = {
  success: 'bg-green-50 text-green-700',
  failure: 'bg-red-50 text-red-700',
  pending: 'bg-yellow-50 text-yellow-700',
};

const ACTION_ICONS = {
  LOGIN: 'LogIn', LOGIN_FAILED: 'LogIn', LOGOUT: 'LogOut',
  CREATE: 'Plus', UPDATE: 'Edit', DELETE: 'Trash2',
  ASSIGN: 'UserCheck', APPROVE: 'CheckCircle', REJECT: 'XCircle',
  EXPORT: 'Download', IMPORT: 'Upload', RESET_DEMO: 'RefreshCw', VIEW: 'Eye',
};

const ACTION_COLORS = {
  LOGIN: 'text-green-600 bg-green-50', LOGIN_FAILED: 'text-red-600 bg-red-50',
  LOGOUT: 'text-gray-600 bg-gray-50', CREATE: 'text-blue-600 bg-blue-50',
  UPDATE: 'text-orange-600 bg-orange-50', DELETE: 'text-red-600 bg-red-50',
  ASSIGN: 'text-purple-600 bg-purple-50', APPROVE: 'text-green-600 bg-green-50',
  REJECT: 'text-red-600 bg-red-50', EXPORT: 'text-indigo-600 bg-indigo-50',
  IMPORT: 'text-teal-600 bg-teal-50', RESET_DEMO: 'text-yellow-600 bg-yellow-50',
  VIEW: 'text-gray-600 bg-gray-50',
};

const DEFAULT_PRESETS = [
  { id: 'errors-today', name: 'Errors Today', icon: 'AlertTriangle', filters: { filterActions: [], filterSeverities: ['error', 'critical'], filterStatuses: [], filterResources: [], userSearch: '', dateFrom: new Date()?.toISOString()?.split('T')?.[0], dateTo: new Date()?.toISOString()?.split('T')?.[0] } },
  { id: 'login-failures', name: 'Login Failures', icon: 'LogIn', filters: { filterActions: ['LOGIN_FAILED'], filterSeverities: [], filterStatuses: ['failure'], filterResources: [], userSearch: '', dateFrom: '', dateTo: '' } },
  { id: 'data-changes', name: 'Data Changes', icon: 'Edit', filters: { filterActions: ['CREATE', 'UPDATE', 'DELETE'], filterSeverities: [], filterStatuses: [], filterResources: [], userSearch: '', dateFrom: '', dateTo: '' } },
  { id: 'this-week', name: 'This Week', icon: 'Calendar', filters: { filterActions: [], filterSeverities: [], filterStatuses: [], filterResources: [], userSearch: '', dateFrom: new Date(Date.now() - 7 * 86400000)?.toISOString()?.split('T')?.[0], dateTo: new Date()?.toISOString()?.split('T')?.[0] } },
];

const formatDate = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  return d?.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatRelative = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const StatCard = ({ icon, label, value, color }) => (
  <div className="bg-card rounded-xl border border-border p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
        <Icon name={icon} size={16} color="currentColor" />
      </div>
    </div>
    <div className="text-2xl font-bold text-foreground">{value}</div>
  </div>
);

const MultiSelectDropdown = ({ label, options, selected, onChange, icon }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref?.current && !ref?.current?.contains(e?.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (val) => {
    onChange(selected?.includes(val) ? selected?.filter(v => v !== val) : [...selected, val]);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg bg-background text-sm transition-smooth ${
          selected?.length > 0 ? 'border-primary text-primary bg-primary/5' : 'border-border text-foreground hover:border-primary/40'
        }`}
      >
        {icon && <Icon name={icon} size={13} color="currentColor" />}
        <span>{label}</span>
        {selected?.length > 0 && (
          <span className="bg-primary text-primary-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
            {selected?.length}
          </span>
        )}
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={12} color="currentColor" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-xl shadow-elevation-2 z-50 min-w-[180px] py-1">
          {options?.map(opt => (
            <button
              key={opt}
              onClick={() => toggle(opt)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-smooth text-left"
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                selected?.includes(opt) ? 'bg-primary border-primary' : 'border-border'
              }`}>
                {selected?.includes(opt) && <Icon name="Check" size={10} color="white" />}
              </div>
              <span className="text-foreground">{opt}</span>
            </button>
          ))}
          {selected?.length > 0 && (
            <div className="border-t border-border mt-1 pt-1">
              <button onClick={() => onChange([])} className="w-full px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground text-left hover:bg-muted transition-smooth">
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AuditActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [filterActions, setFilterActions] = useState([]);
  const [filterSeverities, setFilterSeverities] = useState([]);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterResources, setFilterResources] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(1);
  const [savedPresets, setSavedPresets] = useState(DEFAULT_PRESETS);
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [activePreset, setActivePreset] = useState(null);
  const PAGE_SIZE = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        ?.from('audit_logs')
        ?.select('*', { count: 'exact' })
        ?.order('created_at', { ascending: false });

      if (filterActions?.length === 1) query = query?.eq('action', filterActions?.[0]);
      if (filterSeverities?.length === 1) query = query?.eq('severity', filterSeverities?.[0]);
      if (filterStatuses?.length === 1) query = query?.eq('status', filterStatuses?.[0]);
      if (filterResources?.length === 1) query = query?.eq('resource_type', filterResources?.[0]);
      if (dateFrom) query = query?.gte('created_at', dateFrom);
      if (dateTo) query = query?.lte('created_at', dateTo + 'T23:59:59');

      const { data, error: err } = await query;
      if (err) throw err;
      setLogs(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [filterActions, filterSeverities, filterStatuses, filterResources, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [search, userSearch, filterActions, filterSeverities, filterStatuses, filterResources, dateFrom, dateTo]);

  const filteredLogs = logs?.filter(log => {
    const matchSearch = !search || [
      log?.action, log?.resource_type, log?.resource_name, log?.user_email, log?.user_name
    ]?.some(v => v?.toLowerCase()?.includes(search?.toLowerCase()));

    const matchUser = !userSearch || [
      log?.user_email, log?.user_name
    ]?.some(v => v?.toLowerCase()?.includes(userSearch?.toLowerCase()));

    const matchActions = filterActions?.length === 0 || filterActions?.includes(log?.action);
    const matchSeverities = filterSeverities?.length === 0 || filterSeverities?.includes(log?.severity);
    const matchStatuses = filterStatuses?.length === 0 || filterStatuses?.includes(log?.status);
    const matchResources = filterResources?.length === 0 || filterResources?.includes(log?.resource_type);

    return matchSearch && matchUser && matchActions && matchSeverities && matchStatuses && matchResources;
  });

  const totalPages = Math.ceil((filteredLogs?.length || 0) / PAGE_SIZE);
  const paginatedLogs = filteredLogs?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = {
    total: logs?.length || 0,
    errors: logs?.filter(l => l?.severity === 'error' || l?.severity === 'critical')?.length || 0,
    warnings: logs?.filter(l => l?.severity === 'warning')?.length || 0,
    failures: logs?.filter(l => l?.status === 'failure')?.length || 0,
  };

  const uniqueActions = [...new Set(logs?.map(l => l?.action))]?.filter(Boolean)?.sort();
  const uniqueResources = [...new Set(logs?.map(l => l?.resource_type))]?.filter(Boolean)?.sort();

  const hasActiveFilters = search || userSearch || filterActions?.length > 0 || filterSeverities?.length > 0 || filterStatuses?.length > 0 || filterResources?.length > 0 || dateFrom || dateTo;

  const clearFilters = () => {
    setSearch('');
    setUserSearch('');
    setFilterActions([]);
    setFilterSeverities([]);
    setFilterStatuses([]);
    setFilterResources([]);
    setDateFrom('');
    setDateTo('');
    setActivePreset(null);
  };

  const applyPreset = (preset) => {
    setActivePreset(preset?.id);
    setSearch('');
    setUserSearch(preset?.filters?.userSearch || '');
    setFilterActions(preset?.filters?.filterActions || []);
    setFilterSeverities(preset?.filters?.filterSeverities || []);
    setFilterStatuses(preset?.filters?.filterStatuses || []);
    setFilterResources(preset?.filters?.filterResources || []);
    setDateFrom(preset?.filters?.dateFrom || '');
    setDateTo(preset?.filters?.dateTo || '');
  };

  const saveCurrentPreset = () => {
    if (!presetName?.trim()) return;
    const newPreset = {
      id: `custom-${Date.now()}`,
      name: presetName?.trim(),
      icon: 'Bookmark',
      filters: { filterActions, filterSeverities, filterStatuses, filterResources, userSearch, dateFrom, dateTo }
    };
    setSavedPresets(prev => [...prev, newPreset]);
    setPresetName('');
    setShowSavePreset(false);
    setActivePreset(newPreset?.id);
  };

  const deletePreset = (id) => {
    setSavedPresets(prev => prev?.filter(p => p?.id !== id));
    if (activePreset === id) setActivePreset(null);
  };

  const exportCSV = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Resource Type', 'Resource Name', 'Severity', 'Status', 'Details'];
    const rows = filteredLogs?.map(l => [
      formatDate(l?.created_at), l?.user_email || '', l?.action || '',
      l?.resource_type || '', l?.resource_name || '', l?.severity || '',
      l?.status || '', JSON.stringify(l?.details || {}),
    ]);
    const csv = [headers, ...rows]?.map(r => r?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet><title>Audit Activity Log - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-16 min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Icon name="Activity" size={16} color="#ea580c" />
                </div>
                <span className="text-xs font-semibold text-orange-600 uppercase tracking-wider">System</span>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Audit Activity Log</h1>
              <p className="text-muted-foreground text-sm mt-1">Track all user actions, data changes, and system events.</p>
            </div>
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-lg font-medium hover:bg-primary/90 transition-smooth"
            >
              <Icon name="Download" size={16} color="white" />
              Export CSV
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon="Activity" label="Total Events" value={stats?.total} color="bg-blue-100 text-blue-600" />
            <StatCard icon="AlertTriangle" label="Errors" value={stats?.errors} color="bg-red-100 text-red-600" />
            <StatCard icon="AlertCircle" label="Warnings" value={stats?.warnings} color="bg-yellow-100 text-yellow-600" />
            <StatCard icon="XCircle" label="Failures" value={stats?.failures} color="bg-orange-100 text-orange-600" />
          </div>

          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
              <button onClick={() => setError('')} className="ml-auto"><Icon name="X" size={14} color="var(--color-destructive)" /></button>
            </div>
          )}

          {/* Saved Filter Presets */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Bookmark" size={14} color="var(--color-muted-foreground)" />
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Saved Filters</span>
              <button
                onClick={() => setShowSavePreset(!showSavePreset)}
                className="ml-auto flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <Icon name="Plus" size={12} color="currentColor" />
                Save Current
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {savedPresets?.map(preset => (
                <div key={preset?.id} className="flex items-center gap-0.5">
                  <button
                    onClick={() => activePreset === preset?.id ? clearFilters() : applyPreset(preset)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-l-lg text-xs font-medium border transition-smooth ${
                      activePreset === preset?.id
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-foreground border-border hover:border-primary/40 hover:bg-muted'
                    }`}
                  >
                    <Icon name={preset?.icon} size={11} color="currentColor" />
                    {preset?.name}
                  </button>
                  {!DEFAULT_PRESETS?.find(d => d?.id === preset?.id) && (
                    <button
                      onClick={() => deletePreset(preset?.id)}
                      className="px-1.5 py-1.5 rounded-r-lg border border-l-0 border-border bg-card hover:bg-red-50 hover:text-red-500 text-muted-foreground transition-smooth"
                    >
                      <Icon name="X" size={10} color="currentColor" />
                    </button>
                  )}
                  {DEFAULT_PRESETS?.find(d => d?.id === preset?.id) && (
                    <div className="w-px h-6 bg-border" />
                  )}
                </div>
              ))}
            </div>
            {showSavePreset && (
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="text"
                  value={presetName}
                  onChange={e => setPresetName(e?.target?.value)}
                  placeholder="Preset name..."
                  className="px-3 py-1.5 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-48"
                  onKeyDown={e => e?.key === 'Enter' && saveCurrentPreset()}
                />
                <button onClick={saveCurrentPreset} className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90">
                  Save
                </button>
                <button onClick={() => setShowSavePreset(false)} className="px-3 py-1.5 border border-border rounded-lg text-sm text-muted-foreground hover:bg-muted">
                  Cancel
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            {/* Main Log Table */}
            <div className="flex-1 min-w-0">
              <div className="bg-card rounded-xl border border-border">
                {/* Filters */}
                <div className="p-4 border-b border-border space-y-3">
                  {/* Search Row */}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                      <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search by action, resource..."
                        value={search}
                        onChange={e => setSearch(e?.target?.value)}
                        className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    <div className="relative flex-1">
                      <Icon name="User" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search by user email or name..."
                        value={userSearch}
                        onChange={e => setUserSearch(e?.target?.value)}
                        className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-smooth whitespace-nowrap">
                        <Icon name="X" size={14} color="currentColor" />
                        Clear All
                      </button>
                    )}
                  </div>

                  {/* Multi-select Filter Row */}
                  <div className="flex flex-wrap gap-2">
                    <MultiSelectDropdown
                      label="Actions"
                      options={uniqueActions}
                      selected={filterActions}
                      onChange={setFilterActions}
                      icon="Activity"
                    />
                    <MultiSelectDropdown
                      label="Resources"
                      options={uniqueResources}
                      selected={filterResources}
                      onChange={setFilterResources}
                      icon="Database"
                    />
                    <MultiSelectDropdown
                      label="Severity"
                      options={['info', 'warning', 'error', 'critical']}
                      selected={filterSeverities}
                      onChange={setFilterSeverities}
                      icon="AlertTriangle"
                    />
                    <MultiSelectDropdown
                      label="Status"
                      options={['success', 'failure', 'pending']}
                      selected={filterStatuses}
                      onChange={setFilterStatuses}
                      icon="CheckCircle"
                    />
                  </div>

                  {/* Date Range Row */}
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Icon name="Calendar" size={13} color="currentColor" />
                      <span>Date Range:</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={e => setDateFrom(e?.target?.value)}
                        className="px-2.5 py-1.5 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <span className="text-xs text-muted-foreground">to</span>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={e => setDateTo(e?.target?.value)}
                        className="px-2.5 py-1.5 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>
                    {/* Quick date shortcuts */}
                    <div className="flex gap-1">
                      {[
                        { label: 'Today', days: 0 },
                        { label: '7d', days: 7 },
                        { label: '30d', days: 30 },
                      ]?.map(({ label, days }) => (
                        <button
                          key={label}
                          onClick={() => {
                            const today = new Date()?.toISOString()?.split('T')?.[0];
                            const from = days === 0 ? today : new Date(Date.now() - days * 86400000)?.toISOString()?.split('T')?.[0];
                            setDateFrom(from);
                            setDateTo(today);
                          }}
                          className="px-2 py-1 text-xs border border-border rounded-lg bg-background text-muted-foreground hover:text-foreground hover:border-primary/40 transition-smooth"
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      <span className="font-semibold text-foreground">{filteredLogs?.length}</span> events found
                      {hasActiveFilters && <span className="text-primary ml-1">(filtered)</span>}
                    </p>
                    <button onClick={fetchLogs} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-smooth">
                      <Icon name="RefreshCw" size={12} color="currentColor" />
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Log Entries */}
                {loading ? (
                  <div className="p-12 text-center">
                    <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin mx-auto mb-3" />
                    <p className="text-muted-foreground">Loading audit logs...</p>
                  </div>
                ) : paginatedLogs?.length === 0 ? (
                  <div className="p-12 text-center">
                    <Icon name="Activity" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                    <p className="text-muted-foreground">No audit events found.</p>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="mt-2 text-sm text-primary hover:underline">Clear filters</button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {paginatedLogs?.map(log => {
                      const actionIcon = ACTION_ICONS?.[log?.action] || 'Activity';
                      const actionColor = ACTION_COLORS?.[log?.action] || 'text-gray-600 bg-gray-50';
                      return (
                        <div
                          key={log?.id}
                          onClick={() => setSelectedLog(selectedLog?.id === log?.id ? null : log)}
                          className={`p-4 cursor-pointer hover:bg-muted/40 transition-smooth ${selectedLog?.id === log?.id ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${actionColor}`}>
                              <Icon name={actionIcon} size={14} color="currentColor" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm text-foreground">{log?.action}</span>
                                <span className="text-xs text-muted-foreground">on</span>
                                <span className="text-sm font-medium text-foreground truncate max-w-48">{log?.resource_name || log?.resource_type}</span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 flex-wrap">
                                <span className="text-xs text-muted-foreground">{log?.user_email || 'System'}</span>
                                <span className="text-xs text-muted-foreground">·</span>
                                <span className="text-xs text-muted-foreground">{formatRelative(log?.created_at)}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${SEVERITY_STYLES?.[log?.severity] || ''}`}>{log?.severity}</span>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES?.[log?.status] || ''}`}>{log?.status}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-border flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted disabled:opacity-40 transition-smooth">
                        <Icon name="ChevronLeft" size={14} color="currentColor" />
                      </button>
                      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted disabled:opacity-40 transition-smooth">
                        <Icon name="ChevronRight" size={14} color="currentColor" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Detail Panel */}
            {selectedLog && (
              <div className="lg:w-80 flex-shrink-0">
                <div className="bg-card rounded-xl border border-border sticky top-24">
                  <div className="p-4 border-b border-border flex items-center justify-between">
                    <h3 className="font-heading font-semibold text-foreground">Event Details</h3>
                    <button onClick={() => setSelectedLog(null)} className="p-1 hover:bg-muted rounded">
                      <Icon name="X" size={16} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ACTION_COLORS?.[selectedLog?.action] || 'text-gray-600 bg-gray-50'}`}>
                        <Icon name={ACTION_ICONS?.[selectedLog?.action] || 'Activity'} size={18} color="currentColor" />
                      </div>
                      <div>
                        <p className="font-bold text-foreground">{selectedLog?.action}</p>
                        <p className="text-xs text-muted-foreground">{selectedLog?.resource_type}</p>
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">Resource</span><span className="font-medium text-right max-w-40 truncate">{selectedLog?.resource_name || '-'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">User</span><span className="font-medium text-right max-w-40 truncate">{selectedLog?.user_email || 'System'}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Time</span><span className="font-medium text-xs text-right">{formatDate(selectedLog?.created_at)}</span></div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Severity</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${SEVERITY_STYLES?.[selectedLog?.severity] || ''}`}>{selectedLog?.severity}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Status</span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_STYLES?.[selectedLog?.status] || ''}`}>{selectedLog?.status}</span>
                      </div>
                      {selectedLog?.ip_address && (
                        <div className="flex justify-between"><span className="text-muted-foreground">IP Address</span><span className="font-medium">{selectedLog?.ip_address}</span></div>
                      )}
                    </div>

                    {selectedLog?.details && Object.keys(selectedLog?.details)?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Details</p>
                        <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                          {Object.entries(selectedLog?.details)?.map(([k, v]) => (
                            <div key={k} className="flex justify-between text-xs gap-2">
                              <span className="text-muted-foreground capitalize">{k?.replace(/_/g, ' ')}</span>
                              <span className="font-medium text-right break-all">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default AuditActivityLog;
